import React, { useMemo, useState } from 'react';
import api from '../api';
import './CompanyOnboarding.css';

export default function CompanySetupForm({
  user,
  pendingJoinRequest = null,
  onComplete,
  submitLabel = 'Continue',
}) {
  const [mode, setMode] = useState(pendingJoinRequest ? 'join_existing' : 'primary_owner');
  const [companyName, setCompanyName] = useState(pendingJoinRequest?.requested_company_name || '');
  const [companyWebsite, setCompanyWebsite] = useState(pendingJoinRequest?.requested_company_website || '');
  const [requestNote, setRequestNote] = useState(pendingJoinRequest?.request_note || '');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  const helperCopy = useMemo(() => {
    if (mode === 'join_existing') {
      return 'Tell us the company you are joining. We will queue a join request for review instead of exposing existing companies in the product.';
    }
    return 'Create a new company profile if you are the primary account owner. This creates a new CRM company row and makes you the owner.';
  }, [mode]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');

    if (!companyName.trim()) {
      setError('Company name is required.');
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await api.post('/api/onboarding/company', {
        mode,
        company_name: companyName.trim(),
        company_website: companyWebsite.trim(),
        request_note: requestNote.trim(),
      });
      if (onComplete) {
        onComplete(response.data);
      }
    } catch (requestError) {
      setError(requestError?.response?.data?.error || 'Failed to complete onboarding.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form className="company-onboarding-form" onSubmit={handleSubmit}>
      <div className="company-onboarding-mode-row">
        <button
          type="button"
          className={mode === 'primary_owner' ? 'active' : ''}
          onClick={() => setMode('primary_owner')}
        >
          I&apos;m the owner
        </button>
        <button
          type="button"
          className={mode === 'join_existing' ? 'active' : ''}
          onClick={() => setMode('join_existing')}
        >
          Join existing company
        </button>
      </div>

      <div className="company-onboarding-helper">
        {helperCopy}
      </div>

      <label className="company-onboarding-field">
        <span>Company name</span>
        <input
          type="text"
          value={companyName}
          onChange={(event) => setCompanyName(event.target.value)}
          placeholder="Enter your company name"
          autoFocus
        />
      </label>

      <label className="company-onboarding-field">
        <span>Company site (Optional)</span>
        <input
          type="text"
          value={companyWebsite}
          onChange={(event) => setCompanyWebsite(event.target.value)}
          placeholder="https://yourcompany.com"
        />
      </label>

      {mode === 'join_existing' ? (
        <label className="company-onboarding-field">
          <span>Context for the request (Optional)</span>
          <input
            type="text"
            value={requestNote}
            onChange={(event) => setRequestNote(event.target.value)}
            placeholder={`Example: I'm joining as a sales manager (${user?.email || 'email on file'})`}
          />
        </label>
      ) : null}

      {mode === 'join_existing' ? (
        <div className="company-onboarding-individual">
          <p>
            We do not expose company search in-product. Your request will be stored for review, and a Real Torch
            admin can attach you to the correct company record later if needed.
          </p>
        </div>
      ) : null}

      {error ? <div className="company-onboarding-error">{error}</div> : null}

      <button type="submit" className="company-onboarding-submit" disabled={isSubmitting}>
        {isSubmitting
          ? (mode === 'join_existing' ? 'Submitting request...' : 'Creating company...')
          : (mode === 'join_existing' ? 'Submit join request' : submitLabel)}
      </button>
    </form>
  );
}
